import React from 'react'
import { useQuery } from 'react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Cake, Gift } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/shared/EmptyState'
import { employeeService } from '@/services/employeeService'

const TodaysBirthdaysCard = () => {
  // Fetch employees with birthdays today
  const { data: birthdayData, isLoading, error } = useQuery(
    ['todaysBirthdays'],
    () => employeeService.getTodaysBirthdays(),
    {
      refetchInterval: 60 * 60 * 1000, // Refetch every hour
      onError: (err) => {
        toast.error('Failed to fetch today\'s birthdays: ' + (err.response?.data?.error || err.message))
      }
    }
  )

  const data = birthdayData?.data

  if (error) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <p className="text-destructive">Failed to load today's birthdays</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-pink-100">
            <Cake className="h-5 w-5 text-pink-600" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold">Today's Birthdays</CardTitle>
            <p className="text-sm text-muted-foreground">
              {dayjs().format('MMMM DD, YYYY')}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : data?.employees?.length === 0 ? (
          <EmptyState
            icon="calendar"
            title="No birthdays today"
            description="Check back tomorrow for celebrations!"
          />
        ) : (
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-50 border border-pink-100 mb-4">
              <span className="text-sm font-medium text-pink-700">
                {data?.count || 0} celebration{data?.count !== 1 ? 's' : ''} today!
              </span>
            </div>
            
            <div className="space-y-3">
              {data?.employees?.map((employee, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-gradient-to-br from-pink-400 to-pink-600 text-white">
                      <Gift className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium text-foreground">{employee.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {employee.department} {employee.job_title && `• ${employee.job_title}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default TodaysBirthdaysCard
